import React, { useMemo } from 'react';
import {
  ScatterChart as ReScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  TooltipProps,
} from 'recharts';
import { cn } from '../../../../lib/utils';
import { smallTextSize, tertiaryText, primaryText } from '../../../../styling/stylingHelper';
import { getColorForIndex } from '../../../../utils/chartColors';
import { useChartTheme } from '../../../../utils/chartTheme';
import { ScatterPoint } from '../../../../services/admin/cloudAgentTypes';

export interface ScatterChartProps {
  points: ScatterPoint[];
  xLabel?: string;
  yLabel?: string;
  emptyText?: string;
}

interface ChartPoint extends ScatterPoint {
  fill: string;
}

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload as ChartPoint;
  return (
    <div className="bg-background dark:bg-background-dark rounded-lg shadow-md p-2 border border-border dark:border-border-dark">
      <p className={cn(primaryText, 'text-sm font-medium')}>{p.name}</p>
      <p className={cn(tertiaryText, 'text-xs')}>{p.quadrant}</p>
    </div>
  );
};

/**
 * Dumb scatter chart (data via props). Visual model taken from
 * `components/pages/charts/MenuEngineeringChart.tsx` but recomputes NOTHING — it plots
 * the hydrated `points` (x/y/quadrant/color). Median reference lines are derived from
 * the points purely for visual quadrant guidance (not a displayed metric).
 */
export const ScatterChart: React.FC<ScatterChartProps> = ({ points, xLabel, yLabel, emptyText }) => {
  const theme = useChartTheme();
  const data: ChartPoint[] = useMemo(
    () => points.map((p, i) => ({ ...p, fill: p.color || getColorForIndex(i) })),
    [points],
  );

  const { xMid, yMid } = useMemo(() => {
    if (data.length === 0) return { xMid: 0, yMid: 0 };
    const xs = data.map((d) => d.x);
    const ys = data.map((d) => d.y);
    return {
      xMid: xs.reduce((a, b) => a + b, 0) / xs.length,
      yMid: ys.reduce((a, b) => a + b, 0) / ys.length,
    };
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-8">
        <p className={cn(smallTextSize, tertiaryText)}>{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="h-[300px] sm:h-[396px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ReScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 8 }}>
            <ReferenceLine x={xMid} stroke={theme.referenceLine} strokeDasharray="5 5" strokeWidth={2} />
            <ReferenceLine y={yMid} stroke={theme.referenceLine} strokeDasharray="5 5" strokeWidth={2} />
            <XAxis
              dataKey="x"
              type="number"
              name={xLabel}
              tick={{ fontSize: 12, fill: theme.axisTick }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              dataKey="y"
              type="number"
              name={yLabel}
              tick={{ fontSize: 12, fill: theme.axisTick }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={data}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Scatter>
          </ReScatterChart>
        </ResponsiveContainer>
      </div>
      {(xLabel || yLabel) && (
        <div className={cn('flex justify-between mt-1', tertiaryText, 'text-xs')}>
          <span>{yLabel}</span>
          <span>{xLabel}</span>
        </div>
      )}
    </div>
  );
};
